import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress
} from '@mui/material';
import {
  Send,
  CheckCircle,
  Schedule,
  AttachMoney,
  AutoAwesome
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { brand, text, border } from '../../theme/colors';
import BidList, { GROUPS, groupCounts } from './BidList';

/**
 * Contractor Quotations Page
 *
 * One header, one grouped status filter, and stat cards derived from the
 * authoritative quotation list. The list itself is rendered by BidList, which
 * this page feeds with the fetched bids so there is a single fetch and a
 * single source of truth.
 *
 * NOTE: user-facing copy says "quotation"; internal names stay "bid".
 */
const ContractorBidsPage = () => {
  const { getApiClient, user } = useAuth();
  const navigate = useNavigate();
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [bids, setBids] = useState([]);
  // Win rate from the analytics endpoint (null when the call failed — B1 fix
  // means it now succeeds for contractors, but we still fall back locally).
  const [analyticsWinRate, setAnalyticsWinRate] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const apiClient = getApiClient();
      const [analyticsResponse, bidsResponse] = await Promise.all([
        apiClient.get(`/bids/analytics/contractor/${user.id}`).catch(() => ({ data: { success: false } })),
        apiClient.get('/bids/contractor/me').catch(() => ({ data: { success: false, data: [] } }))
      ]);

      setBids(bidsResponse.data?.success ? (bidsResponse.data.data || []) : []);

      // pg numeric comes back as a string — coerce before the finite check
      const winRate = analyticsResponse.data?.success
        ? parseFloat(analyticsResponse.data.data?.win_rate_percentage)
        : NaN;
      setAnalyticsWinRate(Number.isFinite(winRate) ? winRate : null);
    } catch (error) {
      console.error('Failed to load quotations:', error);
      setBids([]);
      setAnalyticsWinRate(null);
    } finally {
      setLoading(false);
    }
  }, [getApiClient, user?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Derived stats — the list is authoritative, so compute from it directly.
  const counts = groupCounts(bids);
  const acceptedCount = counts.accepted || 0;
  const rejectedCount = bids.filter((b) => b.status === 'rejected').length;
  const withClientCount = counts.with_client || 0;
  const totalValue = bids
    .filter((b) => b.status !== 'withdrawn')
    .reduce((sum, b) => sum + (parseFloat(b.total_cost) || 0), 0);
  const winRate = analyticsWinRate != null
    ? analyticsWinRate
    : Math.round((acceptedCount / Math.max(1, acceptedCount + rejectedCount)) * 100);

  const statCards = [
    {
      title: 'Total quotations',
      value: bids.length,
      caption: 'All quotations',
      color: brand.primary,
      icon: <Send sx={{ fontSize: 40, opacity: 0.3, color: brand.primary }} />,
    },
    {
      title: 'Accepted',
      value: acceptedCount,
      caption: `Win rate ${winRate}%`,
      color: '#43B581',
      icon: <CheckCircle sx={{ fontSize: 40, opacity: 0.3, color: '#43B581' }} />,
    },
    {
      title: 'With client',
      value: withClientCount,
      caption: 'Awaiting client response',
      color: '#FAA61A',
      icon: <Schedule sx={{ fontSize: 40, opacity: 0.3, color: '#FAA61A' }} />,
    },
    {
      title: 'Total value',
      value: `$${totalValue.toLocaleString()}`,
      caption: 'All quotations',
      color: brand.secondary,
      icon: <AttachMoney sx={{ fontSize: 40, opacity: 0.3, color: brand.secondary }} />,
    },
  ];

  const renderStatsCards = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {statCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <Card sx={{
              backgroundColor: 'white',
              color: text.primary,
              border: `2px solid ${border.light}`,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                borderColor: text.primary
              }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: card.color, fontWeight: 500, mb: 1, fontSize: '0.875rem' }}>
                      {card.title}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: card.color }}>
                      {card.value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: text.secondary, fontSize: '0.75rem' }}>
                      {card.caption}
                    </Typography>
                  </Box>
                  <Box sx={{ position: 'relative', top: 10, right: 10 }}>
                    {card.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Single header: title + Find Work aligned right, one subtitle. */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Quotations
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Finish draft quotations, submit ready quotations to clients, and respond when clients request changes.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AutoAwesome />}
          onClick={() => navigate('/contractor/marketplace')}
          sx={{ background: brand.gradient, whiteSpace: 'nowrap' }}
        >
          Find Work
        </Button>
      </Box>

      {renderStatsCards()}

      {/* Grouped status filter — count pills, one vocabulary shared with BidList. */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        {GROUPS.map((g) => {
          const selected = selectedGroup === g.key;
          const count = counts[g.key] ?? 0;
          return (
            <Chip
              key={g.key}
              label={`${g.label} (${count})`}
              clickable
              onClick={() => setSelectedGroup(g.key)}
              sx={{
                fontWeight: 700,
                px: 0.5,
                bgcolor: selected ? brand.primary : 'transparent',
                color: selected ? 'white' : text.primary,
                border: '1.5px solid',
                borderColor: selected ? brand.primary : border.default,
                '&:hover': { bgcolor: selected ? brand.primary : `${brand.primary}14` }
              }}
            />
          );
        })}
      </Box>

      <BidList
        group={selectedGroup}
        bids={bids}
        hideHeader
        onBidChange={loadData}
      />
    </Container>
  );
};

export default ContractorBidsPage;
